import { isSerializedXYWH, type SerializedXYWH } from '@blocksuite/global/gfx';
import type {
  CanvasDragPreviewElementState,
  CanvasDragPreviewState,
  RawAwarenessState,
} from '@blocksuite/store';

export const CANVAS_DRAG_PREVIEW_STALE_MS = 15_000;

export type AwarenessStateEntry = readonly [number, RawAwarenessState];

export function createCanvasDragPreviewState(input: {
  dragId: string;
  elements: CanvasDragPreviewElementState[];
  operation?: CanvasDragPreviewState['operation'];
  startCursor: { x: number; y: number };
  status?: CanvasDragPreviewState['status'];
  updatedAt: number;
  translation?: { x: number; y: number };
  cursor?: { x: number; y: number };
  basisVersion?: number;
}): CanvasDragPreviewState {
  return {
    dragId: input.dragId,
    elements: input.elements,
    elementIds: input.elements.map(element => element.elementId),
    operation: input.operation ?? 'move',
    startCursor: input.startCursor,
    status: input.status ?? 'dragging',
    updatedAt: input.updatedAt,
    translation: input.translation,
    cursor: input.cursor,
    basisVersion: input.basisVersion,
  };
}

export function isCanvasDragPreviewActive(
  preview: CanvasDragPreviewState | undefined,
  now: number,
  staleMs = CANVAS_DRAG_PREVIEW_STALE_MS
): preview is CanvasDragPreviewState {
  return !!preview && now - preview.updatedAt <= staleMs;
}

export function getActiveRemoteCanvasDragPreviewEntries(input: {
  localClientId: number;
  now: number;
  states: Iterable<AwarenessStateEntry>;
  staleMs?: number;
}): AwarenessStateEntry[] {
  const staleMs = input.staleMs ?? CANVAS_DRAG_PREVIEW_STALE_MS;
  const entries: AwarenessStateEntry[] = [];
  for (const [clientId, state] of input.states) {
    if (clientId === input.localClientId) {
      continue;
    }
    if (
      isCanvasDragPreviewActive(state.canvasDragPreview, input.now, staleMs)
    ) {
      entries.push([clientId, state]);
    }
  }
  return entries;
}

export function hasRemoteCanvasDragPreviewLock(input: {
  elementIds: readonly string[];
  localClientId: number;
  now: number;
  states: Iterable<AwarenessStateEntry>;
  staleMs?: number;
}): boolean {
  const requested = new Set(input.elementIds);
  return getActiveRemoteCanvasDragPreviewEntries(input).some(([, state]) => {
    const preview = state.canvasDragPreview;
    return (
      preview?.status === 'dragging' &&
      preview.elementIds.some(elementId => requested.has(elementId))
    );
  });
}

export function resolveCanvasDragPreviewXYWH(input: {
  element: CanvasDragPreviewElementState;
}): SerializedXYWH | null {
  return isSerializedXYWH(input.element.xywh) ? input.element.xywh : null;
}
